import {
  addUpdatableAttribute,
  escapeHtmlText,
  removeUpdatableAttribute,
  renameUpdatableSection,
  replaceElementContent,
  setElementAttribute,
  setElementAttributes,
  type AttributeChange,
} from '@/entities/section-picker';
import type { CustomAttribute } from './customAttributes';

export const SELECTION_HELPER_ATTR = 'data-selection-helper';

export interface ElementConfigDraft {
  /**
   * Slug to tag the element with. Empty on an already-tagged element means UNTAG it; empty on an
   * untagged one leaves it untagged.
   */
  sectionName: string;
  /** Replacement inner content, or null when content is not being changed. */
  content: string | null;
  /**
   * Whether `content` is markup or plain text.
   *
   * Plain text arrives already decoded (it is read out of the DOM as `textContent`), so writing it
   * back into the source verbatim would promote it to live markup — an `&lt;b&gt;` the author sees
   * as literal text would return as a real, unclosed `<b>` element in the sent email. Mirrors the
   * `hasChildElements` decision the modal uses to seed the field, so seeding and writing cannot
   * disagree about which one it is.
   */
  contentIsHtml: boolean;
  /** Replacement image `src`, or null when the image is not being changed. */
  imageSrc: string | null;
  /** Image description: null when not applicable, '' to clear an existing one. */
  imageDescription: string | null;
  customAttributes: CustomAttribute[];
}

export interface ApplyElementConfigParams {
  html: string;
  /** Selector the element was reached by (positional for an untagged element). */
  selector: string;
  /** Slug the element already carries, or null when untagged. */
  currentSectionName: string | null;
  /** Editable attributes as they were when the modal opened, so removals can be detected. */
  originalAttributes: CustomAttribute[];
  draft: ElementConfigDraft;
}

export type ApplyElementConfigResult =
  | { success: true; html: string; sectionName: string | null }
  | { success: false; error?: string };

function sameName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * Apply one modal submission to the template HTML.
 *
 * ORDER IS LOAD-BEARING. A tagged element is addressed by `[data-updatable-<slug>]`, so the moment
 * the tag is added or renamed every previously-derived selector for that element is stale. Tagging
 * therefore runs FIRST and every later step re-derives the selector from the resulting slug.
 *
 * Tagging first also means the likeliest rejection — a duplicate slug — happens before anything is
 * written, so a refused submission cannot leave the template half-mutated.
 *
 * UNTAGGING INVERTS THAT, and is the one exception. A tagged element's selector *is*
 * `[data-updatable-<slug>]`, so dropping the attribute up front destroys the only handle we have
 * and any content/attribute edit in the same submission would silently fail to land. So when the
 * name is cleared, everything else is applied first and the untag happens LAST.
 */
export function applyElementConfig({
  html,
  selector,
  currentSectionName,
  originalAttributes,
  draft,
}: ApplyElementConfigParams): ApplyElementConfigResult {
  let nextHtml = html;

  const requestedName = draft.sectionName.trim();
  // Clearing the name on a tagged element means "stop making this updatable".
  const isUntagging = !!currentSectionName && !requestedName;
  const nextSectionName = isUntagging ? null : requestedName || currentSectionName;

  if (currentSectionName && requestedName && requestedName !== currentSectionName) {
    const renamed = renameUpdatableSection(nextHtml, currentSectionName, requestedName);
    if (!renamed.success) return { success: false, error: renamed.error };
    nextHtml = renamed.html;
  } else if (!currentSectionName && requestedName) {
    const tagged = addUpdatableAttribute(nextHtml, selector, requestedName);
    if (!tagged.success) return { success: false, error: tagged.error };
    nextHtml = tagged.html;
  }

  // Once tagged, the slug selector is unique and immune to positional drift. While untagging the
  // element still carries its old slug at this point, so that selector is the handle to use.
  const targetSelector = isUntagging
    ? `[data-updatable-${currentSectionName}]`
    : nextSectionName
      ? `[data-updatable-${nextSectionName}]`
      : selector;

  if (draft.content !== null) {
    const body = draft.contentIsHtml ? draft.content : escapeHtmlText(draft.content);
    const replaced = replaceElementContent(nextHtml, targetSelector, body);
    if (!replaced.success) return { success: false, error: replaced.error };
    nextHtml = replaced.html;
  }

  if (draft.imageSrc !== null) {
    // eslint-disable-next-line i18next/no-literal-string -- HTML attribute name
    const applied = setElementAttribute(nextHtml, targetSelector, 'src', draft.imageSrc);
    if (!applied.success) return { success: false, error: applied.error };
    nextHtml = applied.html;
  }

  // An empty description removes the attribute so clearing it actually works. Removing goes through
  // the same writer as setting, so a clear that cannot find its element reports failure instead of
  // handing back the input as though it had worked.
  if (draft.imageDescription !== null) {
    const description = draft.imageDescription.trim();
    const applied = setElementAttributes(nextHtml, targetSelector, [
      { name: SELECTION_HELPER_ATTR, value: description || null },
    ]);
    if (!applied.success) return { success: false, error: applied.error };
    nextHtml = applied.html;
  }

  // Gathered into ONE call. Applying them one at a time re-parsed and re-masked the whole document
  // per attribute, and each removal silently no-opped when the element could not be resolved.
  // Untouched attributes are left out: rewriting one strips and re-appends it, reordering the tag
  // and churning the saved HTML for nothing.
  const attributeChanges: AttributeChange[] = [
    ...originalAttributes
      .filter(
        (original) => !draft.customAttributes.some((attr) => sameName(attr.name, original.name)),
      )
      .map((original) => ({ name: original.name, value: null })),
    ...draft.customAttributes
      .filter((attribute) => {
        const previous = originalAttributes.find((original) =>
          sameName(original.name, attribute.name),
        );
        return !previous || previous.value !== attribute.value;
      })
      .map((attribute) => ({ name: attribute.name, value: attribute.value })),
  ];
  if (attributeChanges.length > 0) {
    const applied = setElementAttributes(nextHtml, targetSelector, attributeChanges);
    if (!applied.success) return { success: false, error: applied.error };
    nextHtml = applied.html;
  }

  if (isUntagging) {
    // Helper first: it is addressed via the updatable attribute, so stripping the tag ahead of it
    // would leave it stranded on the element with nothing able to reach it. Same order as
    // `useTemplateEditorState.removeSection`.
    //
    // The two strips have different failure modes, and that asymmetry is the trap: the helper strip
    // is selector-scoped and refuses an element it cannot locate in the source, while the tag strip
    // is a global regex that always lands. So the selector-scoped one runs FIRST and its refusal
    // aborts the whole untag — otherwise an unlocatable element came out with its tag gone and its
    // helper stranded, unreachable through the attribute that had just been removed, and this
    // reported success.
    const stripped = setElementAttributes(nextHtml, targetSelector, [
      { name: SELECTION_HELPER_ATTR, value: null },
    ]);
    if (!stripped.success) return { success: false, error: stripped.error };
    nextHtml = removeUpdatableAttribute(stripped.html, currentSectionName);
  }

  return { success: true, html: nextHtml, sectionName: nextSectionName };
}
